import { useState, useEffect, useRef, useMemo } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import { Button } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useTourHandler } from '@/contexts/tour-handler'
import {
    createProcess,
    updateProcess,
    getProcess,
    getItemModels,
} from '../../../services'
import { useCreateProcessTour } from '../../../hooks/use-create-process-tour'
import {
    CreateProcessRequest,
    UpdateProcessRequest,
    ProcessType,
    EndpointRequest,
    ProcessItemRequest,
    ImportItemMetadata,
    ProcessMetadata,
} from '../../../types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import ProcessTypeSelector from './ProcessTypeSelector'
import ProcessFormFields from './ProcessFormFields'
import EndpointSelector from './EndpointSelector'
import ItemSelector from './ItemSelector'
import SelectedItemsTable from './SelectedItemsTable'
import ItemForm from './ItemForm'
import ImportItemsTable from './ImportItemsTable'
import DraftRestoreDialog from './DraftRestoreDialog'
import { useProcessDraftPersistence } from './use-process-draft-persistence'

// Key localStorage cho bản nháp Process Details (create mode).
const PROCESS_DRAFT_KEY = 'warehouse-process-draft-v1'

export interface CreateProcessFormData {
    type: ProcessType
    process_date: string
    expected_date?: string
    reason: string
    notes: string
    description: string
    source?: EndpointRequest
    destination?: EndpointRequest
    items: ProcessItemRequest[]
}

// Helper to check if process type is an import type
const isImportType = (type: ProcessType): boolean => {
    return type === 'import_purchase' || type === 'import_return'
}

// Endpoint validation requirements per process type
export const getEndpointRequirements = (type: ProcessType) => {
    switch (type) {
        case 'import_purchase':
        case 'import_return':
            return { sourceRequired: true, destinationRequired: true }
        case 'internal_transfer':
            return { sourceRequired: true, destinationRequired: true }
        case 'inventory_check':
            return { sourceRequired: true, destinationRequired: false }
        default:
            return { sourceRequired: false, destinationRequired: false }
    }
}

// Helper to convert date value to RFC3339 format for Go backend
// Handles string, Date, dayjs objects from datepicker
const toRFC3339 = (
    dateValue: string | Date | dayjs.Dayjs | undefined | null
): string | undefined => {
    if (!dateValue) return undefined
    // Use dayjs to normalize any date format and convert to RFC3339
    const d = dayjs(dateValue)
    if (!d.isValid()) return undefined
    return d.format('YYYY-MM-DD') + 'T00:00:00Z'
}

const CreateProcess = () => {
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditMode = Boolean(id)
    const [importItems, setImportItems] = useState<ImportItemMetadata[]>([])
    const [editingItemIndex, setEditingItemIndex] = useState<number | null>(
        null
    )

    // Ref vì clearDraft được tạo (bởi hook draft) sau khi định nghĩa mutation onSuccess.
    const clearDraftRef = useRef<() => void>(() => {})

    // Fetch existing process for edit mode
    const { data: existingProcess, isLoading: isLoadingProcess } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.WAREHOUSE_PROCESSES, id],
            queryFn: () => getProcess(id!),
            enabled: isEditMode,
        })

    // Fetch models for EditItemSheet
    const { data: models = [], isLoading: isLoadingModels } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.WAREHOUSE_MODELS],
                queryFn: getItemModels,
                staleTime: 5 * 60 * 1000,
            },
            { showLoading: false, showError: false }
        )

    // Default form values (memoized) — reused by react-hook-form and the draft-restore check.
    const draftDefaults = useMemo<CreateProcessFormData>(
        () => ({
            type: 'import_purchase',
            process_date: new Date().toISOString().split('T')[0],
            expected_date: '',
            reason: '',
            notes: '',
            description: '',
            source: undefined,
            destination: undefined,
            items: [],
        }),
        []
    )

    const methods = useForm<CreateProcessFormData>({
        defaultValues: draftDefaults,
    })

    const { watch, setValue, reset } = methods
    const selectedType = watch('type')
    const selectedItems = watch('items')
    const source = watch('source')
    const destination = watch('destination')

    // Tour guide integration
    const { startTour } = useCreateProcessTour()
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler for keyboard shortcut (Ctrl+Shift+H)
    useEffect(() => {
        if (!isEditMode) {
            registerHandler(startTour)
            return () => unregisterHandler()
        }
    }, [registerHandler, unregisterHandler, startTour, isEditMode])

    // Populate form with existing process data in edit mode
    useEffect(() => {
        if (existingProcess && isEditMode) {
            reset({
                type: existingProcess.type as ProcessType,
                process_date: existingProcess.process_date?.split('T')[0] || '',
                expected_date:
                    existingProcess.expected_date?.split('T')[0] || '',
                reason: existingProcess.reason || '',
                notes: existingProcess.notes || '',
                description: existingProcess.description || '',
                source: existingProcess.source
                    ? {
                          type: existingProcess.source.type as
                              | 'Location'
                              | 'Supplier'
                              | 'Customer',
                          location_id: existingProcess.source.location_id,
                          supplier_id: existingProcess.source.supplier_id,
                          customer_id: existingProcess.source.customer_id,
                      }
                    : undefined,
                destination: existingProcess.destination
                    ? {
                          type: existingProcess.destination.type as
                              | 'Location'
                              | 'Supplier'
                              | 'Customer',
                          location_id: existingProcess.destination.location_id,
                          supplier_id: existingProcess.destination.supplier_id,
                          customer_id: existingProcess.destination.customer_id,
                      }
                    : undefined,
                items:
                    existingProcess.items?.map((item) => ({
                        item_id: item.item_id,
                        quantity: item.quantity || 1,
                        condition: item.condition,
                        notes: item.notes,
                        // Read rack/position from metadata for transfer processes
                        rack_id: item.metadata?.target_rack_id || item.rack_id,
                        position:
                            item.metadata?.target_position_u || item.position,
                    })) || [],
            })

            // Load import items from metadata if available
            if (
                existingProcess.metadata &&
                isImportType(existingProcess.type as ProcessType)
            ) {
                const metadata = existingProcess.metadata as ProcessMetadata
                if (metadata.items) {
                    setImportItems(metadata.items)
                }
            }
        }
    }, [existingProcess, isEditMode, reset])

    // Create mutation
    const { mutateAsync: submitCreate, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: (data: CreateProcessRequest) => createProcess(data),
            },
            {
                successMessage: 'Process created successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_PROCESSES,
                onSuccess: (data) => {
                    clearDraftRef.current()
                    navigate(`/warehouse/processes/${data.id}`)
                },
            }
        )

    // Update mutation
    const { mutateAsync: submitUpdate, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: (data: UpdateProcessRequest) =>
                    updateProcess(id!, data),
            },
            {
                successMessage: 'Process updated successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_PROCESSES,
                onSuccess: () => {
                    navigate(`/warehouse/processes/${id}`)
                },
            }
        )

    const isPending = isCreating || isUpdating

    // Save draft (localStorage) cho thẻ Process Details — chỉ ở chế độ tạo mới.
    const { shouldRestore, acceptRestore, declineRestore, clearDraft } =
        useProcessDraftPersistence({
            form: methods,
            defaultValues: draftDefaults,
            storageKey: PROCESS_DRAFT_KEY,
            enabled: !isEditMode,
        })
    clearDraftRef.current = clearDraft

    const onSubmit = async () => {
        // Clear previous manual errors before re-validating
        methods.clearErrors()
        const data = methods.getValues()
        let hasError = false

        // Validate reason
        if (!data.reason?.trim()) {
            methods.setError('reason', { message: 'Reason is required' })
            hasError = true
        }

        // Validate required endpoints based on process type
        const config = getEndpointRequirements(data.type)
        if (config.sourceRequired && !data.source) {
            methods.setError('source', { message: 'Source is required' })
            hasError = true
        }
        if (config.destinationRequired && !data.destination) {
            methods.setError('destination', {
                message: 'Destination is required',
            })
            hasError = true
        }

        // Validate at least 1 item
        const hasItems = isImportType(data.type)
            ? importItems.length > 0
            : data.items.length > 0
        if (!hasItems) {
            methods.setError('items', {
                message: 'At least 1 item is required',
            })
            hasError = true
        }

        if (hasError) return

        if (isEditMode) {
            // Update existing process
            const updateRequest: UpdateProcessRequest = {
                process_date: toRFC3339(data.process_date),
                expected_date: toRFC3339(data.expected_date),
                reason: data.reason || undefined,
                notes: data.notes || undefined,
                description: data.description || undefined,
                source: data.source,
                destination: data.destination,
                items: data.items.length > 0 ? data.items : undefined,
            }

            // For import types, include updated metadata with items
            if (isImportType(data.type) && importItems.length > 0) {
                updateRequest.metadata = { items: importItems }
            }

            await submitUpdate(updateRequest)
        } else {
            // Create new process
            const createRequest: CreateProcessRequest = {
                type: data.type,
                process_date: toRFC3339(data.process_date)!,
                expected_date: toRFC3339(data.expected_date),
                reason: data.reason || undefined,
                notes: data.notes || undefined,
                description: data.description || undefined,
                source: data.source,
                destination: data.destination,
                items: data.items.length > 0 ? data.items : undefined,
            }

            // For import types, include metadata with new items to be created on approval
            if (isImportType(data.type) && importItems.length > 0) {
                createRequest.metadata = { items: importItems }
            }

            await submitCreate(createRequest)
        }
    }

    // Handlers for import items
    const handleAddImportItem = (item: ImportItemMetadata) => {
        setImportItems([...importItems, item])
        methods.clearErrors('items')
    }

    const handleRemoveImportItem = (index: number) => {
        setImportItems(importItems.filter((_, i) => i !== index))
    }

    const handleEditImportItem = (index: number) => {
        setEditingItemIndex(index)
    }

    const handleUpdateImportItem = (updatedItem: ImportItemMetadata) => {
        if (editingItemIndex === null) return
        setImportItems((prev) => {
            const updated = [...prev]
            updated[editingItemIndex] = updatedItem
            return updated
        })
        setEditingItemIndex(null)
    }

    const handleAddItem = (item: ProcessItemRequest) => {
        const existingItems = methods.getValues('items')
        const existingIndex = existingItems.findIndex(
            (i) => i.item_id === item.item_id
        )

        if (existingIndex >= 0) {
            // Update quantity if item exists
            const newItems = [...existingItems]
            newItems[existingIndex] = {
                ...newItems[existingIndex],
                quantity:
                    (newItems[existingIndex].quantity || 1) +
                    (item.quantity || 1),
            }
            setValue('items', newItems)
        } else {
            // Add new item
            setValue('items', [...existingItems, item])
        }
        methods.clearErrors('items')
    }

    const handleRemoveItem = (itemId: string) => {
        const existingItems = methods.getValues('items')
        setValue(
            'items',
            existingItems.filter((i) => i.item_id !== itemId)
        )
    }

    const handleUpdateItemQuantity = (itemId: string, quantity: number) => {
        const existingItems = methods.getValues('items')
        const newItems = existingItems.map((i) =>
            i.item_id === itemId ? { ...i, quantity } : i
        )
        setValue('items', newItems)
    }

    const handleUpdateItemRack = (
        itemId: string,
        rackId: string | undefined
    ) => {
        const existingItems = methods.getValues('items')
        const newItems = existingItems.map((i) =>
            i.item_id === itemId
                ? {
                      ...i,
                      rack_id: rackId,
                      position: undefined,
                      parent_item_id: undefined,
                  }
                : i
        )
        setValue('items', newItems)
    }

    const handleUpdateItemPosition = (
        itemId: string,
        position: number | undefined
    ) => {
        const existingItems = methods.getValues('items')
        const newItems = existingItems.map((i) =>
            i.item_id === itemId ? { ...i, position } : i
        )
        setValue('items', newItems)
    }

    const handleUpdateParentItemId = (
        itemId: string,
        parentItemId: string | undefined
    ) => {
        const existingItems = methods.getValues('items')
        const newItems = existingItems.map((i) =>
            i.item_id === itemId
                ? {
                      ...i,
                      parent_item_id: parentItemId,
                      position: parentItemId ? undefined : i.position,
                  }
                : i
        )
        setValue('items', newItems)
    }

    // Disable type change after items are added or in edit mode
    const isTypeLocked =
        isEditMode || selectedItems.length > 0 || importItems.length > 0

    // Show loading state while fetching process for edit mode
    if (isEditMode && isLoadingProcess) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Loading process...</div>
            </div>
        )
    }

    // Check if process can be edited (only draft or pending_approval)
    const canEdit =
        !isEditMode ||
        existingProcess?.status === 'draft' ||
        existingProcess?.status === 'pending_approval'

    if (isEditMode && !canEdit) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="text-muted-foreground">
                    Cannot edit process in '{existingProcess?.status}' status
                </div>
                <Button
                    variant="outlined"
                    onClick={() => navigate(`/warehouse/processes/${id}`)}
                >
                    Back to Process
                </Button>
            </div>
        )
    }

    return (
        <div>
            <DraftRestoreDialog
                open={shouldRestore}
                onAccept={acceptRestore}
                onDecline={declineRestore}
            />

            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.warehouse,
                        title: 'Capacities & Resources',
                    },
                    {
                        path: paths.warehouseProcesses,
                        title: 'Processes',
                    },
                    ...(isEditMode
                        ? [
                              {
                                  path: `/warehouse/processes/${id}`,
                                  title: existingProcess?.code || 'Process',
                              },
                          ]
                        : []),
                    {
                        path: '',
                        title: isEditMode ? 'Edit' : 'Create Process',
                    },
                ]}
            />

            <div
                className={`grid mt-1 ${editingItemIndex !== null ? 'grid-cols-12 gap-2' : 'grid-cols-1 gap-2'}`}
            >
                {/* Left Column - Form */}
                <div
                    className={
                        editingItemIndex !== null ? 'col-span-8' : 'col-span-1'
                    }
                >
                    <FormProvider {...methods}>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault()
                                onSubmit()
                            }}
                            className="space-y-2"
                            data-tour="warehouse-process-form"
                        >
                            {/* Process Type Selection */}
                            <Card data-tour="warehouse-process-type-card">
                                <CardHeader>
                                    <CardTitle>Process Type</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ProcessTypeSelector
                                        disabled={isTypeLocked}
                                        selectedType={selectedType}
                                        onTypeChange={(type) =>
                                            setValue('type', type)
                                        }
                                    />
                                    {isTypeLocked && (
                                        <p className="text-sm text-muted-foreground mt-2">
                                            Process type is locked after adding
                                            items. Remove all items to change
                                            type.
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Process Details */}
                            <Card data-tour="warehouse-process-details-card">
                                <CardHeader>
                                    <CardTitle>Process Details</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ProcessFormFields />
                                </CardContent>
                            </Card>

                            {/* Source & Destination */}
                            <Card data-tour="warehouse-source-destination-card">
                                <CardHeader>
                                    <CardTitle>Source & Destination</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <EndpointSelector
                                        processType={selectedType}
                                    />
                                </CardContent>
                            </Card>

                            {/* Item Selection */}
                            <Card data-tour="warehouse-items-card">
                                <CardHeader>
                                    <CardTitle>Items</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {isImportType(selectedType) ? (
                                        <>
                                            <div className="text-sm text-muted-foreground mb-2">
                                                Add new items to import. Items
                                                will be created when approved.
                                            </div>
                                            <ItemForm
                                                mode="create"
                                                onAdd={handleAddImportItem}
                                            />
                                            <ImportItemsTable
                                                items={importItems}
                                                onRemove={
                                                    handleRemoveImportItem
                                                }
                                                onEdit={handleEditImportItem}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <ItemSelector
                                                processType={selectedType}
                                                sourceLocationId={
                                                    source?.location_id
                                                }
                                                onSelect={handleAddItem}
                                            />
                                            <SelectedItemsTable
                                                items={selectedItems}
                                                onRemove={handleRemoveItem}
                                                onUpdateQuantity={
                                                    handleUpdateItemQuantity
                                                }
                                                processType={selectedType}
                                                destinationLocationId={
                                                    destination?.location_id
                                                }
                                                onUpdateRack={
                                                    handleUpdateItemRack
                                                }
                                                onUpdatePosition={
                                                    handleUpdateItemPosition
                                                }
                                                onUpdateParentItemId={
                                                    handleUpdateParentItemId
                                                }
                                            />
                                        </>
                                    )}
                                    {methods.formState.errors.items && (
                                        <p className="text-sm text-destructive">
                                            {methods.formState.errors.items.message?.toString()}
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Form Actions */}
                            <div
                                className="flex gap-2 justify-end pt-2"
                                data-tour="warehouse-form-actions"
                            >
                                <Button
                                    variant="outlined"
                                    type="button"
                                    onClick={() =>
                                        navigate(
                                            isEditMode
                                                ? `/warehouse/processes/${id}`
                                                : paths.warehouseProcesses
                                        )
                                    }
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    loading={isPending}
                                >
                                    {isEditMode
                                        ? 'Update Process'
                                        : 'Create Process'}
                                </Button>
                            </div>
                        </form>
                    </FormProvider>
                </div>

                {/* Right Column - Edit Panel (sticky so it stays visible while scrolling) */}
                {editingItemIndex !== null && (
                    <div className="col-span-4 sticky top-16 self-start max-h-[calc(100vh-5rem)] overflow-y-auto">
                        <ItemForm
                            mode="edit"
                            item={importItems[editingItemIndex]}
                            onAdd={handleUpdateImportItem}
                            onCancel={() => setEditingItemIndex(null)}
                            models={models}
                            isLoadingModels={isLoadingModels}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default CreateProcess
